'use client';

import React, { useEffect, useRef, useState } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [request, setRequest] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => firstInputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !request.trim()) return;

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim() && !emailRegex.test(email.trim())) {
      setEmailError('Введите корректный email адрес');
      return;
    }
    setEmailError('');

    const emailLine = email.trim() ? `\nEmail: ${email.trim()}` : '';
    const tgMessage = `Новая заявка с сайта:\nИмя: ${name}${emailLine}\nЗапрос: ${request}`;
    const tgUrl = `https://t.me/drgii?text=${encodeURIComponent(tgMessage)}`;
    window.open(tgUrl, '_blank', 'noopener,noreferrer');
    setSubmitted(true);
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setEmailError('');
    setRequest('');
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(30, 18, 40, 0.75)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Форма обратной связи"
    >
      <div className="relative w-full max-w-md bg-plum border border-gold/20 rounded-2xl shadow-2xl overflow-hidden">
        {/* Decorative top bar */}
        <div className="h-1 w-full bg-gradient-to-r from-gold/40 via-gold to-gold/40" />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-parchment/50 hover:text-parchment transition-colors"
          aria-label="Закрыть"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div className="px-8 py-8">
          {!submitted ? (
            <>
              {/* Header */}
              <div className="text-center mb-7">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="h-px w-8 bg-gold/50" />
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-heather-light">
                    Обратная связь
                  </span>
                  <span className="h-px w-8 bg-gold/50" />
                </div>
                <h2 className="font-serif text-2xl text-parchment mb-2">
                  Расскажите о вашем запросе
                </h2>
                <p className="text-sm text-heather-light/80 font-serif italic">
                  Мы свяжемся с вами в ближайшее время
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.15em] text-heather-light mb-2">
                    Ваше имя
                  </label>
                  <input
                    ref={firstInputRef}
                    id="contact-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Как вас зовут?"
                    required
                    className="w-full bg-white/5 border border-gold/20 rounded-lg px-4 py-3 text-parchment placeholder-parchment/30 text-sm focus:outline-none focus:border-gold/60 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.15em] text-heather-light mb-2">
                    Email <span className="normal-case text-heather-light/50">(необязательно)</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    placeholder="your@email.com"
                    className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-parchment placeholder-parchment/30 text-sm focus:outline-none transition-colors ${emailError ? 'border-red-400/60 focus:border-red-400' : 'border-gold/20 focus:border-gold/60'}`}
                  />
                  {emailError && (
                    <p className="mt-1.5 text-xs text-red-400">{emailError}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="contact-request" className="block text-xs uppercase tracking-[0.15em] text-heather-light mb-2">
                    Ваш запрос
                  </label>
                  <textarea
                    id="contact-request"
                    value={request}
                    onChange={(e) => setRequest(e.target.value)}
                    placeholder="Опишите, что вы хотите сделать..."
                    required
                    rows={4}
                    className="w-full bg-white/5 border border-gold/20 rounded-lg px-4 py-3 text-parchment placeholder-parchment/30 text-sm focus:outline-none focus:border-gold/60 transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-gold w-full py-3 rounded-full text-sm font-semibold tracking-wide"
                >
                  Отправить запрос
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-5">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                  <path d="M6 14l6 6 10-12" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="font-serif text-2xl text-parchment mb-3">Спасибо!</h2>
              <p className="text-sm text-heather-light/80 font-serif italic mb-6">
                Ваш запрос открыт в Telegram. Мы ответим вам в ближайшее время.
              </p>
              <button
                onClick={handleClose}
                className="btn-gold px-8 py-3 rounded-full text-sm font-semibold tracking-wide"
              >
                Закрыть
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
